import { getFunctions, httpsCallable } from "firebase/functions";
import { type PartnerRole } from "../data/partnerRoles";
import { firebaseApp } from "./Firebase";

export { INTERVIEWER_ROLES, INTERVIEW_ROUNDS, PARTNER_ROLES, PARTNER_SPECIALTIES, hasInterviewRole, interviewTiersForRoles, mentorRoleForCourseType, partnerRoleLabel } from "../data/partnerRoles";
export type { PartnerRole } from "../data/partnerRoles";

const functions = getFunctions(firebaseApp, "asia-south1");

async function call<TRequest, TResponse>(name: string, data: TRequest): Promise<TResponse> {
    const result = await httpsCallable<TRequest, TResponse>(functions, name)(data);
    return result.data;
}

export type PartnerStatus = "INVITED" | "ACTIVE" | "INACTIVE";
export type PartnerProfile = {
    id: string;
    authUid?: string | null;
    name: string;
    email: string;
    mobile?: string;
    currentCompany?: string;
    professionalTitle?: string;
    bio?: string;
    photoURL?: string;
    status: PartnerStatus;
    roles: PartnerRole[];
    specialties: string[];
    interviewRoundTypes: string[];
    createdAt?: string;
    updatedAt?: string;
};

export type PartnerInput = Omit<PartnerProfile, "id" | "authUid" | "status" | "createdAt" | "updatedAt">;

export type PartnerAssignment = {
    id: string;
    resourceType: "INTERVIEW_SLOT" | "INTERVIEW_BOOKING" | "COURSE_RUN";
    resourceId: string;
    partnerId: string;
    title: string;
    subtitle?: string;
    scheduledAt?: string | null;
    status?: string;
    assignmentRole: "LEAD" | "SUPPORTING" | "PRIMARY" | "COORDINATOR";
    programId?: string;
    cohortId?: string;
    courseType?: "INTERNSHIP" | "WORKSHOP" | "COURSE" | "OTHER";
    endsAt?: string | null;
    courseStatus?: string;
    needsReassignment?: boolean;
};

export type PartnerReferences = {
    programs: Array<{ id: string; name: string; type?: string }>;
    cohorts: Array<{ id: string; name: string; programId?: string; programName?: string; status?: string }>;
    interviewSlots: Array<{ id: string; details?: string; start?: string | null; end?: string | null; status?: string }>;
};

export function listPartners() {
    return call<Record<string, never>, PartnerProfile[]>("listPartners", {});
}

export function createPartner(data: PartnerInput) {
    return call<typeof data, { partner: PartnerProfile; invitationUrl: string }>("createPartner", data);
}

export function updatePartner(data: { partnerId: string; partner: PartnerInput }) {
    return call<typeof data, PartnerProfile>("updatePartner", data);
}

export function setPartnerStatus(data: { partnerId: string; status: "ACTIVE" | "INACTIVE" }) {
    return call<typeof data, PartnerProfile>("setPartnerStatus", data);
}

export function deletePartner(partnerId: string) {
    return call<{ partnerId: string }, { ok: true; deletedRecords: number }>("deletePartner", { partnerId });
}

export function resendPartnerInvite(partnerId: string) {
    return call<{ partnerId: string }, { invitationUrl: string; expiresAt: string }>("resendPartnerInvite", { partnerId });
}

export function getPartnerAdminDetail(partnerId: string) {
    return call<{ partnerId: string }, { partner: PartnerProfile; assignments: PartnerAssignment[] }>("getPartnerAdminDetail", { partnerId });
}

export function listPartnerReferences() {
    return call<Record<string, never>, PartnerReferences>("listPartnerReferences", {});
}

export function assignPartner(data: { partnerId: string; resourceType: "COURSE_RUN"; resourceId: string; assignmentRole?: "LEAD" | "SUPPORTING" | "COORDINATOR" }) {
    return call<typeof data, PartnerAssignment>("assignPartner", data);
}

export function removePartnerAssignment(data: { partnerId: string; assignmentId: string }) {
    return call<typeof data, { ok: true }>("removePartnerAssignment", data);
}

export function activatePartnerInvite(token: string) {
    return call<{ token: string }, PartnerProfile>("activatePartnerInvite", { token });
}

export function getPartnerWorkspace() {
    return call<Record<string, never>, { partner: PartnerProfile; assignments: PartnerAssignment[] }>("getPartnerWorkspace", {});
}

export function listPartnerAssignments(resourceType: "INTERVIEW_SLOT" | "COURSE_RUN", resourceId?: string) {
    return call<{ resourceType: "INTERVIEW_SLOT" | "COURSE_RUN"; resourceId?: string }, PartnerAssignment[]>("listPartnerAssignments", { resourceType, resourceId });
}
